import { supabaseClient } from "@/lib/supabase";
import type { FavouriteUserT } from "@/types/database/profiles";
import { useAuth } from "@/utils/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";

import {
    applyFavouriteCaches,
    invalidateFavouriteCaches,
    isFavouriteUniqueViolation,
    restoreFavouriteCaches,
    snapshotFavouriteCaches,
} from "./favouriteUserCache";

export const insertFavouriteUser = async (newUser: FavouriteUserT) => {
    const { data, error } = await supabaseClient
        .from("favourite_users")
        .upsert(newUser, { onConflict: "favourited_by,favourited_user" })
        .select()
        .single();
    if (error) {
        if (isFavouriteUniqueViolation(error)) return newUser;
        throw error;
    }
    return data;
};

export const useInsertFavouriteUser = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();
    const auth = useAuth();
    return useMutation({
        mutationFn: async (userId: string) =>
            insertFavouriteUser({
                favourited_by: auth.id,
                favourited_user: userId,
            }),
        onMutate: async (userId) => {
            const snapshot = await snapshotFavouriteCaches(queryClient, auth.id);
            applyFavouriteCaches(queryClient, auth.id, userId, true);
            return { snapshot };
        },
        onError: (error, _userId, context) => {
            if (context?.snapshot) {
                restoreFavouriteCaches(queryClient, auth.id, context.snapshot);
            }
            if (isFavouriteUniqueViolation(error)) return;
            toast.error(toastCopy.errorUpdating);
        },
        onSettled: () => {
            invalidateFavouriteCaches(queryClient, auth.id);
        },
    });
};
export default useInsertFavouriteUser;
